import React, { useState } from 'react';
import { Button } from '../UI/Button';
interface EngagementFormProps {
  onFormSubmit: (formData: any) => void;
}
export const EngagementForm = ({
  onFormSubmit
}: EngagementFormProps) => {
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    sex: '',
    phone: '',
    location: '',
    industry: '',
    occupation: '',
    commitment: '',
    mediaConsent: '',
    comments: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const response = await fetch('https://formspree.io/f/mpwyowlw', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        // Call the parent component's callback to move to the next step
        onFormSubmit(formData);
      } else {
        throw new Error('Failed to submit form');
      }
    } catch (err) {
      setError('There was an error submitting your form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="bg-white p-6 rounded-lg border-2 border-gray-300" data-id="element-144" data-mp="2l7nu320wtpfe">
      <h2 className="text-2xl font-bold text-gray-800 mb-4" data-id="element-145" data-mp="2gbypuldy3kcr">
        The Networking Salon — How to Engage
      </h2>
      <div className="mb-6" data-id="element-146" data-mp="2c78vj0yxqdty">
        <p className="text-gray-700 mb-4" data-id="element-147" data-mp="279zadxikwobx">Welcome to The Networking Salon!</p>
        <p className="text-gray-700 mb-4" data-id="element-148" data-mp="27c3bk7p3f98i">
          Hey there! We're so excited to have you as part of our community. This
          is a space full of energy, ideas, and collaboration—where young people
          come together to learn, grow, and lift each other up.
        </p>
        <p className="text-gray-700 mb-4" data-id="element-149" data-mp="xcinicfq0u1s">
          Here we build intimate, high-trust ecosystems that connect people to
          transformative relationships, opportunities, and visibility.
        </p>
        <p className="text-gray-700 mb-4" data-id="element-150" data-mp="ob1c37d9dgjf">
          We host curated salon-style gatherings, peer masterminds, local and
          global sessions, opportunity boards, and feedback loops.
        </p>
        <p className="text-gray-700 mb-4" data-id="element-151" data-mp="ek6onzoa0awe">
          Members gain visibility, access curated grants, fellowships,
          collaborations, and speaking/media opportunities — each one making
          room for another!
        </p>
        <p className="text-gray-700 mb-4" data-id="element-152" data-mp="9mem8bq096dy">
          To keep this space thriving, we've put together a few simple
          guidelines. They're here to help us all get the most out of this
          experience and make sure everyone feels supported, respected, and
          empowered.
        </p>
        <p className="text-gray-700 mb-4" data-id="element-153" data-mp="3d5b39ygydq0h">
          By agreeing to these rules, you're not just joining a community—you're
          helping shape it. Your engagement, positivity, and commitment to these
          values make a real difference.
        </p>
        <p className="text-gray-700 mb-6" data-id="element-154" data-mp="3m7kbuisnv9b8">
          Let's build something great together!
        </p>
      </div>
      <div className="mb-8 border-2 border-dashed border-gray-300 p-6 rounded-lg" data-id="element-155" data-mp="3i347wn2lybyn">
        <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-156" data-mp="3w2m0ldj0y9da">
          Community Engagement Guidelines
        </h3>
        <div className="space-y-4" data-id="element-157" data-mp="3r4u0hw887gy0">
          <p className="flex items-start" data-id="element-158" data-mp="9oce59ynnbf5">
            <span className="mr-2" data-id="element-159" data-mp="9o2nrl9p728x">🌟</span>
            <span className="text-gray-700" data-id="element-160" data-mp="4pixj8o2n4ar">
              <strong data-id="element-161" data-mp="4qu1b8hioc1v">Share Opportunities</strong> Got a lead on a job,
              internship, training, grant, or other opportunity? Don't keep it
              to yourself! Share it with the group—and if you can, recommend
              fellow members for opportunities that match their strengths and
              aspirations.
            </span>
          </p>
          <p className="flex items-start" data-id="element-162" data-mp="2d58vo9oa8eh2">
            <span className="mr-2" data-id="element-163" data-mp="2d3xu8gib7it4">💡</span>
            <span className="text-gray-700" data-id="element-164" data-mp="2hw7k5wjfcive">
              <strong data-id="element-165" data-mp="2hxiihgu3oyf4">Support with Ideas</strong> Offer your insights, advice,
              or creative thinking to help others grow their projects,
              businesses, or careers. This is a space where collaboration fuels
              progress.
            </span>
          </p>
          <p className="flex items-start" data-id="element-166" data-mp="1u7o8r852vpz3">
            <span className="mr-2" data-id="element-167" data-mp="1u8qzng2ehsf0">💬</span>
            <span className="text-gray-700" data-id="element-168" data-mp="1y7yq1gybm2t9">
              <strong data-id="element-169" data-mp="1y6x24bw7hlu4">Engage in Group Conversations</strong> Your voice matters.
              Join discussions, give thoughtful feedback, and help keep our
              conversations rich and inspiring. Active participation helps keep
              our community alive and thriving.
            </span>
          </p>
          <p className="flex items-start" data-id="element-170" data-mp="3eob22og8t8ln">
            <span className="mr-2" data-id="element-171" data-mp="3oqok4bmbvsa6">🤝</span>
            <span className="text-gray-700" data-id="element-172" data-mp="3osj2bi67t6ak">
              <strong data-id="element-173" data-mp="3jr23rtb7dvx5">Attend Networking Salon Events</strong> Whenever possible,
              show up! Our events are great chances to meet new people, share
              ideas, and build meaningful relationships. Your presence adds so
              much to the experience.
            </span>
          </p>
          <p className="flex items-start" data-id="element-174" data-mp="36071izv7md2j">
            <span className="mr-2" data-id="element-175" data-mp="35xuazybdyudv">📢</span>
            <span className="text-gray-700" data-id="element-176" data-mp="310ayb2uc6po1">
              <strong data-id="element-177" data-mp="30zj3hu8kcvyt">Recommend The Networking Salon</strong> Know someone who
              would thrive here? Invite them! Let's grow our circle with
              positive, motivated individuals who can contribute to and benefit
              from the community.
            </span>
          </p>
          <p className="flex items-start" data-id="element-178" data-mp="u9la230phsu5">
            <span className="mr-2" data-id="element-179" data-mp="q92h25n2ugqd">🎁</span>
            <span className="text-gray-700" data-id="element-180" data-mp="q9asru7grfzz">
              <strong data-id="element-181" data-mp="2jri25suzycyu">Share Relevant Products and Events</strong> If you come
              across a service, product, or event that could be useful to others
              here, pass it along! We're all about lifting each other up in ways
              big and small.
            </span>
          </p>
        </div>
        <div className="mt-6 pt-6 border-t border-gray-300" data-id="element-182" data-mp="2erx5jp3augpa">
          <h4 className="text-lg font-bold text-gray-800 mb-3" data-id="element-183" data-mp="2eqcck3wmv5tj">
            Why These Guidelines Matter
          </h4>
          <p className="text-gray-700 mb-4" data-id="element-184" data-mp="37h41qhfa6u7d">
            At The Networking Salon, we're committed to building an inclusive,
            supportive, and opportunity-filled community. These guidelines are
            here to help each of us:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700" data-id="element-185" data-mp="2yf3aq2an54e5">
            <li data-id="element-186" data-mp="1i1msq81g434x">Discover and share valuable opportunities</li>
            <li data-id="element-187" data-mp="1i4irfyl4avuf">Receive feedback and fresh ideas to grow our work</li>
            <li data-id="element-188" data-mp="1n2u0w87i34c4">Connect through meaningful, respectful conversations</li>
            <li data-id="element-189" data-mp="1ejgxmwvc9sez">Build relationships through events and ongoing engagement</li>
            <li data-id="element-190" data-mp="1aix0vueogqtd">Find and offer resources that can help us all succeed</li>
          </ul>
          <p className="text-gray-700 mt-4" data-id="element-191" data-mp="1oieinptk33t8">
            By following these guidelines, you're helping create a space where
            everyone can grow and thrive.
          </p>
        </div>
      </div>
      {error && <div className="bg-gray-100 border-2 border-gray-400 text-gray-800 p-4 rounded-md mb-6" data-id="element-192" data-mp="vthawgmz0peq">
          {error}
        </div>}
      <form onSubmit={handleSubmit} className="space-y-6" data-id="element-193" data-mp="qvpaey89fuyx">
        <div className="space-y-4" data-id="element-194" data-mp="15jk7eqtawlht">
          <div data-id="element-195" data-mp="15klq9hgk6ca3">
            <label htmlFor="email" className="block text-gray-700 font-medium mb-1" data-id="element-196" data-mp="15l4xo48l1i9v">
              Email <span className="text-gray-500" data-id="element-197" data-mp="10s24vybmqcuq">(required)</span>
            </label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-gray-500" data-id="element-198" data-mp="d27r9ejhkk9f" />
          </div>
          <div data-id="element-199" data-mp="3is28o3u4kekp">
            <label htmlFor="fullName" className="block text-gray-700 font-medium mb-1" data-id="element-200" data-mp="3islu3uizhl3h">
              Full Name <span className="text-gray-500" data-id="element-201" data-mp="3nr5ehqhxiotq">(required)</span>
            </label>
            <input type="text" id="fullName" name="fullName" value={formData.fullName} onChange={handleChange} required className="w-full px-4 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-gray-500" data-id="element-202" data-mp="3sn3yl1w85koc" />
          </div>
          <div data-id="element-203" data-mp="aiagcvw0azpe">
            <label className="block text-gray-700 font-medium mb-1" data-id="element-204" data-mp="ai0speyfp4mv">
              Sex <span className="text-gray-500" data-id="element-205" data-mp="f9sjnqli5cdj">(required)</span>
            </label>
            <div className="space-y-2" data-id="element-206" data-mp="fbv7q533gvfm">
              {['Male', 'Female'].map(option => <div key={option} className="flex items-center" data-id="element-207" data-mp="k7u003rjmxlb">
                  <input type="radio" id={`sex-${option}`} name="sex" value={option} checked={formData.sex === option} onChange={handleChange} required className="mr-2 h-4 w-4 border-2 border-gray-300" data-id="element-208" data-mp="p8q34f42mfpo" />
                  <label htmlFor={`sex-${option}`} className="text-gray-700" data-id="element-209" data-mp="2dsdqkdd0g0w6">
                    {option}
                  </label>
                </div>)}
            </div>
          </div>
          <div data-id="element-210" data-mp="24pus05g9cafq">
            <label htmlFor="phone" className="block text-gray-700 font-medium mb-1" data-id="element-211" data-mp="24rfm5tyntrd2">
              Phone Number <span className="text-gray-500" data-id="element-212" data-mp="1zsux8qouyu3x">(required)</span>
            </label>
            <input type="text" id="phone" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-gray-500" data-id="element-213" data-mp="2xfl7ahtmlidf" />
          </div>
          <div data-id="element-214" data-mp="2nnxjcqwa2jgp">
            <label htmlFor="location" className="block text-gray-700 font-medium mb-1" data-id="element-215" data-mp="2nnfghbf416zt">
              City of Residence{' '}
              <span className="text-gray-500" data-id="element-216" data-mp="3g5jfqz1c5dhl">(required)</span>
            </label>
            <select id="location" name="location" value={formData.location} onChange={handleChange} required className="w-full px-4 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-gray-500" data-id="element-217" data-mp="3g7mh2f7xut1m">
              <option value="" data-id="element-218" data-mp="3sjmv2vy8of51">Select your city</option>
              <option value="Houston Texas" data-id="element-219" data-mp="3sl7o2h4wnq0s">Houston Texas</option>
              <option value="Lagos" data-id="element-220" data-mp="bbjalhjlvuf6">Lagos</option>
              <option value="Abuja" data-id="element-221" data-mp="b96oqtaq8zwu">Abuja</option>
              <option value="Benin City" data-id="element-222" data-mp="6b53q2tqqqiq">Benin City</option>
              <option value="Warri" data-id="element-223" data-mp="kby0ku7ilxp0">Warri</option>
              <option value="Berlin" data-id="element-224" data-mp="kamwsue2kpxw">Berlin</option>
              <option value="Port Harcourt" data-id="element-225" data-mp="fcut7342bfdv">Port Harcourt</option>
              <option value="UK" data-id="element-226" data-mp="fcl3lgmxja8p">UK</option>
              <option value="Other" data-id="element-227" data-mp="u1hk8231xvkx">Other</option>
            </select>
          </div>
          <div data-id="element-228" data-mp="p2o565849win">
            <label htmlFor="industry" className="block text-gray-700 font-medium mb-1" data-id="element-229" data-mp="p24munek1643">
              Industry <span className="text-gray-500" data-id="element-230" data-mp="131xlr608isn5">(required)</span>
            </label>
            <input type="text" id="industry" name="industry" value={formData.industry} onChange={handleChange} required className="w-full px-4 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-gray-500" data-id="element-231" data-mp="z0u7gnxu54o1" />
          </div>
          <div data-id="element-232" data-mp="1mn677892yhlw">
            <label htmlFor="occupation" className="block text-gray-700 font-medium mb-1" data-id="element-233" data-mp="1mnpelv13tnlo">
              What do you do? <span className="text-gray-500" data-id="element-234" data-mp="18o6ik0lki2pq">(required)</span>
            </label>
            <textarea id="occupation" name="occupation" value={formData.occupation} onChange={handleChange} required rows={4} className="w-full px-4 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-gray-500" data-id="element-235" data-mp="1dm7y4bjsbjwg" />
          </div>
          <div data-id="element-236" data-mp="pxym21cdiaii">
            <label className="block text-gray-700 font-medium mb-1" data-id="element-237" data-mp="pyi19aan2ksu">
              Are you committed to actively participating in the ways described
              above? <span className="text-gray-500" data-id="element-238" data-mp="txh50rkmwbip">(required)</span>
            </label>
            <div className="space-y-2" data-id="element-239" data-mp="g0an0z7qiguj">
              {['Yes', 'No', 'I have questions'].map(option => <div key={option} className="flex items-center" data-id="element-240" data-mp="kxkc2dr7oqhu">
                  <input type="radio" id={`commitment-${option}`} name="commitment" value={option} checked={formData.commitment === option} onChange={handleChange} required className="mr-2 h-4 w-4 border-2 border-gray-300" data-id="element-241" data-mp="2moks6ikwtkm6" />
                  <label htmlFor={`commitment-${option}`} className="text-gray-700" data-id="element-242" data-mp="2dwsi48zssdi4">
                    {option}
                  </label>
                </div>)}
            </div>
          </div>
          <div data-id="element-243" data-mp="35lvp3wj2k1yg">
            <label className="block text-gray-700 font-medium mb-1" data-id="element-244" data-mp="35mdimleck22o">
              Are you okay with your pictures and videos of you taken at our
              events (in-person or online) be used for promotional purposes?{' '}
              <span className="text-gray-500" data-id="element-245" data-mp="2wo1wavkijqg1">(required)</span>
            </label>
            <div className="space-y-2" data-id="element-246" data-mp="2rmw36wmxy15z">
              {['Yes', 'No'].map(option => <div key={option} className="flex items-center" data-id="element-247" data-mp="1l83f24f6ly3q">
                  <input type="radio" id={`mediaConsent-${option}`} name="mediaConsent" value={option} checked={formData.mediaConsent === option} onChange={handleChange} required className="mr-2 h-4 w-4 border-2 border-gray-300" data-id="element-248" data-mp="1g88qw8msjmmd" />
                  <label htmlFor={`mediaConsent-${option}`} className="text-gray-700" data-id="element-249" data-mp="1b46juziqljhn">
                    {option}
                  </label>
                </div>)}
            </div>
          </div>
          <div data-id="element-250" data-mp="1ktzwm3um84nh">
            <label htmlFor="comments" className="block text-gray-700 font-medium mb-1" data-id="element-251" data-mp="1ktq1y2yml9bg">
              Additional Comments or Suggestions (You can ask your questions
              here): <span className="text-gray-500" data-id="element-252" data-mp="1tstppk8qmcwa">(optional)</span>
            </label>
            <textarea id="comments" name="comments" value={formData.comments} onChange={handleChange} rows={4} className="w-full px-4 py-2 border-2 border-gray-300 rounded-md focus:outline-none focus:border-gray-500" data-id="element-253" data-mp="1ysz9yfcogx4c" />
          </div>
        </div>
        <div className="pt-2" data-id="element-254" data-mp="4g9e3s2r1dub">
          <Button type="submit" disabled={isSubmitting} className="w-full py-3" data-id="element-255" data-mp="4h1c19jtjnnr">
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
      </form>
    </div>;
};